import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { BaseButton } from "src/components/ui/baseButton";
import MainInformation from "./components/MainInformation";
import {
  createGeneralFactSheet,
  useGetGeneralFactSheet,
} from "../services/useFactSheetApi";
import { useDempStore } from "src/context/stores";
import dayjs from "dayjs";
import moment from "moment";

const schema = yup.object({
  taxId: yup.string().required("This is a required field"),
  companyName: yup.string().required("This is a required field"),
  companyAddress: yup.string().required("This is a required field"),
  incorporationDate: yup.object().required("This is a required field"),
});

const defaultGeneralFactSheet = {
  companyImage: "",
  taxId: "",
  companyName: "",
  companyAddress: "",
  numberOfEmployees: "",
  workingDays: "",
  grossSalary: "",
  incorporationDate: null,
};

const GeneralForm = (props) => {
  const { disabled, onClose, selectedFactSheetYear } = props;
  const { control, handleSubmit, setValue, watch, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      defaultGeneralFactSheet,
    },
  });

  const companyId = useDempStore((state) => state.companyId);
  const selectedYear = dayjs(selectedFactSheetYear).format("YYYY");

  const {
    dataGeneralFactSheet,
    isLoadingDataGeneralFactSheet,
    isErrorDataGeneralFactSheet,
    mutateGeneralFactSheet: refetchDataGeneralFactSheet,
  } = useGetGeneralFactSheet(companyId, selectedYear);

  const setFieldValue = () => {
    if (dataGeneralFactSheet) {
      const newIncorporationDate = dataGeneralFactSheet?.data
        ?.incorporation_date
        ? dayjs(dataGeneralFactSheet.data.incorporation_date, "DD-MM-YYYY")
        : null;

      const incorporationDate = newIncorporationDate
        ? {
            day: newIncorporationDate.date(),
            month: newIncorporationDate.month() + 1,
            year: newIncorporationDate.year(),
          }
        : null;

      reset({
        companyImage: dataGeneralFactSheet?.data?.images?.org_logo?.imageUrl,
        taxId: dataGeneralFactSheet?.data?.tax_id,
        companyName: dataGeneralFactSheet?.data?.name,
        companyAddress: dataGeneralFactSheet?.data?.address,
        incorporationDate: incorporationDate,
        numberOfEmployees:
          dataGeneralFactSheet?.data?.number_of_employees ?? "",
        workingDays: dataGeneralFactSheet?.data?.working_days ?? "",
        grossSalary: dataGeneralFactSheet?.data?.gross_salary ?? "",
      });
    } else if (isErrorDataGeneralFactSheet) {
      reset(defaultGeneralFactSheet);
    }
  };

  useEffect(() => {
    setFieldValue();
  }, [dataGeneralFactSheet, isErrorDataGeneralFactSheet]);

  const onSubmit = async (formData) => {
    const newIncorporationDate = {
      ...formData?.incorporationDate,
      month: formData?.incorporationDate?.month - 1,
    };
    const incorporationDate = moment(newIncorporationDate).format("DD-MM-YYYY");

    const logo = formData?.companyImage;

    const newData = {
      name: formData?.companyName,
      address: formData?.companyAddress,
      tax_id: formData?.taxId,
      incorporation_date: incorporationDate,
      number_of_employees: formData?.numberOfEmployees,
      working_days: formData?.workingDays,
      gross_salary: formData?.grossSalary,
    };

    const newDataPayload = {
      company_id: companyId,
      year: Number(selectedYear),
      logo: logo,
      data: newData,
    };

    const response = await createGeneralFactSheet(newDataPayload);

    if (response?.data) {
      await refetchDataGeneralFactSheet();
      onClose();
    } else if (response?.error) {
      console.log(response?.error);
    }
  };

  return (
    <div className="w-full h-[calc(100vh-200px)] p-4">
      <div className="relative max-w-[87.5rem] h-full mx-auto">
        <MainInformation
          control={control}
          disabled={disabled}
          required={true}
        />

        {/* button actions */}
        {!disabled && (
          <div
            className={`w-full flex items-center justify-center gap-5 pt-4 absolute bottom-0 left-0 border-t border-gray-7`}
          >
            <BaseButton
              variant="primary"
              size="normal"
              className="!min-w-[100px]"
              onClick={handleSubmit((formData) => onSubmit(formData))}
            >
              Save
            </BaseButton>

            <BaseButton
              variant="secondary"
              size="normal"
              className="!min-w-[100px]"
              onClick={() => {
                onClose();
                setFieldValue();
              }}
            >
              Cancel
            </BaseButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralForm;
